import PushInvitacion from "./PushInvitacion.jsx";
import PantallaYo from "./sistema/PantallaYo.jsx";
import PushEnApp from "./PushEnApp.jsx";
import { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import {
  ChevronRight, ChevronDown, Settings, Menu, X, LogOut, Home, Plus, Search, Star, Icon,
} from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";
import { trackNav } from "../lib/nav-tracker.js";
import { useUserPref } from "../hooks/useUserPref.js";
import {
  buildNavGroups,
  matchesRole,
  isClientScopedRoute,
  PAGE_TITLES,
  getDefaultFavorites,
} from "../nav/registry.js";
import { CommandPalette } from "./CommandPalette.jsx";
import { SoaLogo } from "./SoaMark.jsx";
import CrewBrain from "./CrewBrain.jsx";
import MotivoCambioHost from "./MotivoCambioHost.jsx";
import EsfuerzoHost from "./EsfuerzoHost.jsx";
import RetiroHost from "./RetiroHost.jsx";
import { useChatResumen } from "../lib/useChatResumen.js";
import VersionBadge from "./VersionBadge.jsx";
import { osLabel } from "../lib/version.js";
import { api } from "../api/client.js";
import { NotificationsDropdown } from "./NotificationsDropdown.jsx";
import { MentionsDropdown } from "./MentionsDropdown.jsx";
import { PedidosDropdown } from "./PedidosDropdown.jsx";
import RachaBanner from "./RachaBanner.jsx";
import { TourAutoStart } from "../academia/TourRunner.jsx";
import { AcademiaProvider } from "../academia/useAcademia.js";
import XpToast     from "./XpToast.jsx";
import GamifCelebration from "./GamifCelebration.jsx";
import { useGamifEvents } from "../hooks/useGamifEvents.js";
import { NotificationsManager } from "./NotificationsManager.jsx";
import { ClientSelector } from "./ClientSelector.jsx";
import { CrearTareaModal } from "./CrearTareaModal.jsx";
import { CrearClienteModal } from "./CrearClienteModal.jsx";
import { CrearProyectoModal } from "./CrearProyectoModal.jsx";
import { Button } from "./ui/button.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar.jsx";
import { Tooltip } from "./ui/tooltip.jsx";
import { useNewFeature } from "./NewBadge.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useClient } from "../context/ClientContext.jsx";
import { useRolNavigation } from "../lib/useRolNavigation.js";
import { useFlag } from "../lib/useFlags.js";
import { useTriage } from "../lib/useTriage.js";
import { BARRAS, barraDeUsuario, aplicarBarra } from "../nav/barras-rol.js";
import { leerVerComo } from "../lib/useAccesoEstado.js";
import BarrasNoche from "./sistema/BarrasNoche.jsx";
import BarraInferior from "./sistema/BarraInferior.jsx";
import { useOSBadges } from "../lib/useOSBadges.js";

/* NAV registry (buildNavGroups, ROUTE_SCOPES, matchesRole, ROLE_ALIASES,
 * isClientScopedRoute, PAGE_TITLES) vive en src/nav/registry.js — importado
 * arriba. Los helpers se usan tal cual estaban antes.
 *
 * SOA 10 PRO · cambio de piel, no de estructura: mismos grupos, nombres y orden.
 * Cambian los contenedores (sidebar 272px sin bordes, barra superior 76px), los grupos que
 * se pliegan con contador (fase 2) y las píldoras. Ver docs/SOA10-PRO.md.
 */

/* ── Score propio (30d) para la tarjeta de usuario: "CEO · score 100" ──────── */
function useMiScore() {
  const [score, setScore] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.cumplimientoMiScore(30).then(r => { if (!cancelled) setScore(r.score ?? null); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return score;
}

/* ── Layout root ────────────────────────────────────────────────────────── */
export default function Layout() {
  const location   = useLocation();
  const {
    miembro, displayName, initials, photoURL, rol, signOut,
    isAdmin, isCOO, isCFO, isCCC, isCuenta, isCM, isCPC, isIT, isHunter, isEspecialista,
  } = useAuth();
  // Temporada 1 · detecta level-up / badge nuevo comparando snapshots (sin push del backend)
  useGamifEvents(miembro?.id);
  const miScore = useMiScore();
  // v7.2.4 · roles combinados (rol + titulo + roles_extra) para sidebar filter
  const allRolesStr = (() => {
    const partes = [];
    if (rol) partes.push(String(rol));
    if (miembro?.titulo) partes.push(String(miembro.titulo));
    let extras = miembro?.roles_extra;
    if (typeof extras === "string") { try { extras = JSON.parse(extras); } catch { extras = []; } }
    if (Array.isArray(extras)) partes.push(...extras.map(String));
    return partes.join(" ");
  })();
  const { selectedCliente, setSelected } = useClient();
  const navBase = useRolNavigation();
  const { badges: badgesBase } = useOSBadges();
  const chatResumen = useChatResumen();
  // Triage (ff_triage): reemplaza la bandeja personal en la 2ª posición de «Mi Espacio» y en cada barra por rol.
  const triageOn = useFlag("ff_triage");
  const barraInferiorOn = useFlag("ff_pwa_nav");   // el despeje de 84 px solo se reserva si la barra inferior existe
  const { data: triageData } = useTriage();
  const nav = triageOn ? { ...navBase, miBandeja: "/triage", miBandejaLabel: "Triage" } : navBase;
  const badges = triageOn ? { ...badgesBase, mi_bandeja: triageData?.total ?? 0 } : badgesBase;
  const academiaOn = useFlag("ff_academia");
  // «Tutoriales» solo existe en el menú con ff_academia encendido (apagado = desaparece de todas las barras)
  const NAV_GROUPS_DYN = buildNavGroups(nav, badges)
    .map(g => ({ ...g, items: g.items.filter(i => i.to !== "/ayuda/tutoriales" || academiaOn) }));
  // Badging API (PWA instalada): el contador del ícono de la app es el total de Triage.
  const triageTotal = triageData?.total;
  useEffect(() => {
    if (!triageOn || typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    if (triageTotal > 0) navigator.setAppBadge(triageTotal).catch(() => {});
    else navigator.clearAppBadge?.().catch(() => {});
  }, [triageOn, triageTotal]);
  // Fase 3 · barra por rol: cada rol ve solo lo suyo; «Ver todo el OS» muestra todas las entradas (se recuerda).
  // «Ver como» (modo prueba): un habilitado puede mirar el menú con la barra de otro rol. Solo cambia la
  // navegación en este navegador; los permisos reales no se tocan.
  const verComo = leerVerComo();
  const barraKey = (verComo && BARRAS[verComo]) ? verComo : barraDeUsuario({ isAdmin, isCOO, isCFO, isCCC, isCuenta, isCPC, isCM, isIT, isHunter, isEspecialista });
  const [verTodoRaw, setVerTodo] = useUserPref("nav:ver-todo", false);
  const barraActiva = !!barraKey && verTodoRaw !== true;
  const GRUPOS_VISTOS = barraActiva ? aplicarBarra(NAV_GROUPS_DYN, BARRAS[barraKey], nav) : NAV_GROUPS_DYN;
  const [favoritesRaw, setFavorites, { synced: favoritesSynced }] = useUserPref("nav:favorites", []);
  const [favoritesSeeded, setFavoritesSeeded] = useUserPref("nav:favorites:seeded", false);
  // Fase 2 · grupos que se pliegan. Solo se guarda lo que el usuario cambió a mano.
  const [gruposAbiertosRaw, setGruposAbiertos] = useUserPref("nav:grupos-abiertos", {});
  const gruposAbiertos = gruposAbiertosRaw && typeof gruposAbiertosRaw === "object" && !Array.isArray(gruposAbiertosRaw)
    ? gruposAbiertosRaw : {};
  // Defensive: si backend devuelve algo que no es array (bug prefs antiguos), tratar como vacío.
  const favorites = Array.isArray(favoritesRaw) ? favoritesRaw : [];
  // Semilla de favoritos por rol — una sola vez por usuario, solo si nunca tocó
  // sus favoritos. Después de la primera siembra el usuario controla su lista
  // libremente, incluso si la vacía del todo.
  useEffect(() => {
    if (!favoritesSynced || favoritesSeeded || favorites.length > 0) return;
    setFavorites(getDefaultFavorites({ isAdmin, isCOO, isCFO, isCCC, isCuenta, isCM, isEspecialista }));
    setFavoritesSeeded(true);
  }, [favoritesSynced, favoritesSeeded, favorites.length]);
  const favoritesSet = new Set(favorites);
  const toggleFavorite = (to) => {
    setFavorites(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(to) ? arr.filter(x => x !== to) : [...arr, to];
    });
  };
  // Lookup ruta → item para renderizar favoritos con su icono/label real.
  const NAV_ITEM_BY_PATH = (() => {
    const map = new Map();
    // Mi Día suelto (no está en NAV_GROUPS_DYN pero es favoriteable)
    map.set("/inicio", { to: "/inicio", label: "Mi Día", icon: Home });
    for (const g of NAV_GROUPS_DYN) {
      for (const it of g.items) map.set(it.to, it);
    }
    return map;
  })();
  const favoriteItems = (favorites || [])
    .map(to => NAV_ITEM_BY_PATH.get(to))
    .filter(Boolean);
  const [cmdOpen, setCmdOpen]         = useState(false);
  const [collapsed, setCollapsed]     = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [yoOpen, setYoOpen]           = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const pageTitle = PAGE_TITLES[location.pathname] ?? `THE CREW AGENCIA ${osLabel()}`;

  // Un grupo está abierto si el usuario lo abrió/cerró a mano; si no, por defecto lo están
  // «Hoy», «Operación» y el que contiene la ruta actual (nunca se esconde la página donde estás parado).
  const grupoTieneRutaActiva = (g) => g.items.some(it => {
    const base = String(it.to || "").split("?")[0];
    return base && (location.pathname === base || (base !== "/" && location.pathname.startsWith(base + "/")));
  });
  const grupoAbierto = (g) => (
    typeof gruposAbiertos[g.label] === "boolean"
      ? gruposAbiertos[g.label]
      : barraActiva || g.label === "Hoy" || g.label === "Operación" || grupoTieneRutaActiva(g)   // en la barra por rol hay pocas entradas: todo abierto
  );
  const setGrupo = (label, abierto) => setGruposAbiertos(prev => {
    const base = prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {};
    return { ...base, [label]: abierto };
  });
  const favoritosAbierto = typeof gruposAbiertos["Favoritos"] === "boolean" ? gruposAbiertos["Favoritos"] : true;
  const itemsVisibles = (group) => group.items.filter(item => {
    // lo que ya está en Favoritos no se repite en su grupo (la estrella se quita desde Favoritos)
    if (favoritesSet.has(item.to)) return false;
    if (!item.roles) return true;       // sin restricción → todos lo ven
    // v7.2.4 · multi-rol: considerar rol + titulo + roles_extra (suplencias, etc)
    return matchesRole(allRolesStr, item.roles);
  });
  // «Contraer todo / Expandir todo»: pisa lo que la persona había abierto a mano (se recuerda como cualquier otro cambio).
  const gruposDeLaBarra = GRUPOS_VISTOS.map(g => ({ ...g, items: itemsVisibles(g) })).filter(g => g.items.length > 0);
  const estadoGrupos = [
    ...(favoriteItems.length > 0 ? [["Favoritos", favoritosAbierto]] : []),
    ...gruposDeLaBarra.map(g => [g.label, grupoAbierto(g)]),
  ];
  const todosAbiertos = estadoGrupos.length > 0 && estadoGrupos.every(([, abierto]) => abierto);
  const contraerExpandirTodo = () => setGruposAbiertos(() => Object.fromEntries(estadoGrupos.map(([label]) => [label, !todosAbiertos])));

  /* Global ⌘K / Ctrl+K */
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen(p => !p);
      }
      if (e.key === "Escape") setMobileOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Close mobile overlay when route changes */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Atajos del manifest / enlaces: /?accion=nueva-tarea abre el modal de Nueva tarea.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("accion") === "nueva-tarea") {
      p.delete("accion");
      window.history.replaceState({}, "", location.pathname + (p.toString() ? `?${p}` : ""));
      setTimeout(() => window.dispatchEvent(new CustomEvent("crew-action", { detail: "new-task" })), 400);
    }
  }, [location.search]);

  return (
    <AcademiaProvider>
    {/* La altura descuenta el margen seguro del iPhone que el body ya suma como relleno; con 100vh a secas la pantalla se pasaba ~80 px y el campo de escribir quedaba tapado.
        h-screen queda de respaldo para navegadores sin dvh (el style inválido se ignora). */}
    <div className="flex h-screen bg-background overflow-hidden" style={{ height: "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))" }}>

      {/* ── Mobile overlay ─────────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar (272px, sin bordes, mismo fondo que la app) ─────────── */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-background",
          "transition-[width,transform] duration-300 ease-in-out",
          "lg:relative lg:translate-x-0",
          collapsed ? "w-[84px]" : "w-[272px]",
          mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo row */}
        <div className={cn("flex items-center h-[76px] flex-shrink-0", collapsed ? "justify-center px-2" : "px-5 gap-3")}>
          <SoaLogo collapsed={collapsed} />
          {/* Collapse toggle (desktop) */}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full hover:bg-card text-muted-foreground transition-colors ml-auto flex-shrink-0"
              aria-label="Colapsar menú"
            >
              <ChevronRight className="h-[18px] w-[18px] rotate-180" />
            </button>
          )}
          {/* Close (mobile) */}
          {mobileOpen && (
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden ml-auto p-1.5 rounded-full text-muted-foreground hover:text-foreground"
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="hidden lg:flex mx-auto mb-1 items-center justify-center w-8 h-8 rounded-full hover:bg-card text-muted-foreground transition-colors"
            aria-label="Expandir menú"
          >
            <ChevronRight className="h-[18px] w-[18px]" />
          </button>
        )}

        {/* Client selector — solo aplica en rutas "scope: cliente" */}
        <div className={cn("flex-shrink-0 transition-opacity px-3", collapsed ? "pb-2" : "pb-3",
                           !isClientScopedRoute(location.pathname) && "opacity-60")}
             title={!isClientScopedRoute(location.pathname) ? "Esta página es de agencia — el cliente no aplica" : undefined}>
          <div className="rounded-2xl bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)]">
            <ClientSelector collapsed={collapsed} />
          </div>
        </div>

        {/* Navigation — filtrado por rol del usuario */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
          {/* Mi Día suelto, sin grupo */}
          {!favoritesSet.has("/inicio") && (
            <NavItem
              to="/inicio" label="Mi Día" icon={Home} shortcut="I" collapsed={collapsed}
              isFavorite={false}
              onToggleFavorite={toggleFavorite}
            />
          )}

          {!collapsed && estadoGrupos.length > 1 && (
            <button
              type="button"
              onClick={contraerExpandirTodo}
              aria-label={todosAbiertos ? "Contraer todos los grupos del menú" : "Expandir todos los grupos del menú"}
              data-testid="nav-contraer-expandir"
              className="mt-1 flex h-8 w-full items-center justify-end gap-1 rounded-full px-3 text-[12.5px] font-bold text-[#6B6F7A] transition-colors hover:bg-card/70 hover:text-[#0E7490]"
            >
              <Icon name={todosAbiertos ? "expand_less" : "unfold_more"} className="text-[18px]" />
              {todosAbiertos ? "Contraer todo" : "Expandir todo"}
            </button>
          )}

          {/* ★ Favoritos — solo si hay items pineados */}
          {favoriteItems.length > 0 && (
            <div className="pt-2">
              {!collapsed ? (
                <GrupoHeader
                  label="Favoritos" abierto={favoritosAbierto} cantidad={favoriteItems.length} tono="amber"
                  onClick={() => setGrupo("Favoritos", !favoritosAbierto)}
                />
              ) : <div className="my-2 mx-3 h-px bg-border" />}
              {(collapsed || favoritosAbierto) && favoriteItems.map(item => (
                <NavItem
                  key={`fav:${item.to}`} {...item} collapsed={collapsed}
                  isFavorite
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}

          {GRUPOS_VISTOS.map((group) => {
            const visibleItems = itemsVisibles(group);
            if (visibleItems.length === 0) return null;
            const g = { ...group, items: visibleItems };
            const abierto = grupoAbierto(g);
            return (
              <div key={group.label} className="pt-2">
                {!collapsed ? (
                  <GrupoHeader label={group.label} abierto={abierto} cantidad={visibleItems.length} onClick={() => setGrupo(group.label, !abierto)} />
                ) : <div className="my-2 mx-3 h-px bg-border" />}
                {abierto && visibleItems.map(item => (
                  <NavItem
                    key={item.to} {...item} collapsed={collapsed}
                    isFavorite={favoritesSet.has(item.to)}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
            );
          })}
          {barraKey && (
            <button
              type="button"
              onClick={() => setVerTodo(!(verTodoRaw === true))}
              title={barraActiva ? "Muestra todas las entradas del OS (se recuerda tu elección)" : `Vuelve a la barra de ${BARRAS[barraKey].label}`}
              className={cn(
                "mt-3 w-full flex items-center justify-center gap-2 h-10 rounded-full text-[14px] font-extrabold transition-colors",
                collapsed ? "px-0" : "px-4",
                "bg-card text-[#0E7490] shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:bg-[#E0F7FB]"
              )}
            >
              <Icon name={barraActiva ? "apps" : "view_sidebar"} className="text-[20px]" />
              {!collapsed && (barraActiva ? "Ver todo el OS" : `Mi barra · ${BARRAS[barraKey].label}`)}
            </button>
          )}
        </nav>

        {/* Version badge — abajo, arriba del user footer */}
        <div className="flex-shrink-0 px-3 pb-2">
          <VersionBadge collapsed={collapsed} />
        </div>

        {/* User footer: tarjeta blanca "Malte · CEO · score 100" */}
        <div className="flex-shrink-0 p-3 relative">
          {collapsed ? (
            <Tooltip content={`${displayName} · ${rol ?? "Equipo"}`} side="right">
              <button
                onClick={() => setUserMenuOpen(p => !p)}
                className="w-full flex justify-center"
                aria-label={`${displayName} · ${rol ?? "Equipo"}`}
              >
                <Avatar className="h-11 w-11 cursor-pointer">
                  {photoURL && <AvatarImage src={photoURL} />}
                  <AvatarFallback className="bg-night text-white text-xs font-extrabold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={() => setUserMenuOpen(p => !p)}
              className="w-full flex items-center gap-3 p-2.5 rounded-2xl bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:shadow-md cursor-pointer transition group"
            >
              <Avatar className="h-11 w-11 flex-shrink-0">
                {photoURL && <AvatarImage src={photoURL} />}
                <AvatarFallback className="bg-night text-white text-xs font-extrabold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[15px] font-extrabold text-foreground leading-tight truncate">{displayName}</p>
                <p className="text-[13px] text-muted-foreground leading-tight truncate">
                  {rol ?? "Equipo"}{miScore != null ? ` · score ${miScore}` : ""}
                </p>
              </div>
              <Settings className="h-5 w-5 text-muted-foreground/70 group-hover:text-foreground transition-colors flex-shrink-0" />
            </button>
          )}

          {/* User menu dropdown */}
          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className={cn(
                "absolute bottom-full mb-1 z-[60] bg-popover rounded-2xl shadow-xl shadow-[#111318]/15 py-1.5 min-w-[180px]",
                collapsed ? "left-2" : "left-3 right-3"
              )}>
                <div className="px-3.5 py-2 border-b border-border mb-1">
                  <p className="text-sm font-extrabold text-foreground truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{rol ?? "Equipo"}</p>
                </div>
                <Link
                  to="/yo"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="account_circle" className="text-[18px]" />
                  Yo · mi espacio
                </Link>
                <Link
                  to="/mi-perfil"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="person" className="text-[18px]" />
                  Mi perfil
                </Link>
                <Link
                  to="/settings/notificaciones"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="notifications" className="text-[18px]" />
                  Notificaciones
                </Link>
                <button
                  onClick={() => { setUserMenuOpen(false); window.dispatchEvent(new Event("crew-abrir-bienvenida")); }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="help" className="text-[18px]" />
                  Ver bienvenida
                </button>
                <Link
                  to="/presentacion"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="slideshow" className="text-[18px]" />
                  Presentación del sistema
                </Link>
                <button
                  onClick={() => { signOut(); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <LogOut className="h-[18px] w-[18px]" />
                  Cerrar sesión
                </button>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* ── Main column ────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        <BarrasNoche />

        {/* Topbar (76px, búsqueda en píldora blanca, sin borde) */}
        <header className="flex items-center gap-2 sm:gap-3 px-4 lg:px-6 h-[60px] sm:h-[76px] bg-background flex-shrink-0">
          {/* Celular: el ícono de la app (con el menú cerrado no había marca) · lleva a Mi Día */}
          <Link to="/inicio" aria-label="SOA 10 PRO · Mi Día" className="lg:hidden flex-shrink-0">
            <img src="/favicon.svg" alt="" width="44" height="44" className="h-11 w-11 rounded-[27%] shadow-[0_1px_3px_rgba(17,19,24,0.12)]" />
          </Link>
          {/* Mobile hamburger */}
          <button
            className="lg:hidden grid place-items-center w-11 h-11 rounded-full bg-card text-foreground shadow-[0_1px_3px_rgba(17,19,24,0.06)]"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Migas — «Bernardo › Tareas» (el cliente solo en rutas "scope: cliente") */}
          <div className="flex items-center gap-2 text-[17px] min-w-0">
            {selectedCliente && isClientScopedRoute(location.pathname) ? (
              <>
                <span className="text-muted-foreground font-medium truncate hidden md:inline" title={selectedCliente.nombre}>
                  {selectedCliente.nombre}
                </span>
                <button
                  onClick={() => setSelected(null)}
                  className="hidden md:inline-flex p-0.5 rounded-full hover:bg-card text-muted-foreground/60 hover:text-foreground"
                  title="Ver todos los clientes"
                  aria-label="Ver todos los clientes"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <ChevronRight className="h-[18px] w-[18px] text-muted-foreground/50 hidden md:block flex-shrink-0" />
              </>
            ) : (
              !isClientScopedRoute(location.pathname) && (
                <span className="hidden lg:inline text-[12px] uppercase tracking-wider px-2 py-0.5 bg-secondary text-muted-foreground rounded-full font-extrabold">Agencia</span>
              )
            )}
            <span className="font-extrabold text-foreground truncate hidden sm:inline">{pageTitle}</span>
          </div>

          {/* Búsqueda ⌘K — píldora blanca de 46px, máx. 520px */}
          <button
            onClick={() => setCmdOpen(true)}
            data-tour="cmdk-abrir"
            aria-label="Buscar"
            className="flex items-center justify-center sm:justify-start gap-2.5 h-[44px] w-[44px] sm:h-[46px] sm:w-auto px-0 sm:px-4 rounded-full bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] text-[15px] text-muted-foreground hover:text-foreground transition-all flex-shrink-0 sm:flex-shrink sm:flex-1 min-w-0 max-w-[520px] ml-2 sm:ml-3"
          >
            <Search className="h-5 w-5 flex-shrink-0" />
            <span className="hidden md:inline truncate flex-1 text-left">Buscar tareas, clientes, páginas…</span>
            <kbd className="hidden md:inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[12px] font-bold text-muted-foreground">⌘K</kbd>
          </button>

          {/* Actions */}
          <div className="ml-auto flex items-center gap-1 sm:gap-2.5 flex-shrink-0">
            {/* racha activa (v6.7.2 F2) */}
            {/* En celular no entra: con la lupa nueva el encabezado se pasaba de 390 px y cortaba el botón «+». */}
            <div className="hidden sm:block"><RachaBanner userId={miembro?.id} /></div>

            {/* v8.6.24 · Fase 58 — pedidos nuevos, a la izquierda de @ (decisión
                de producto explícita: separar "nuevo pedido" de "me mencionaron") */}
            <PedidosDropdown />

            {/* @ — bandeja de menciones (jun 2026) */}
            <MentionsDropdown />

            {/* Bell — notifications dropdown */}
            <NotificationsDropdown />

            {/* (?) Procedimientos — la barra por rol lo saca del menú, queda acá */}
            <Link
              to="/procedimientos"
              title="Procedimientos · cómo se hacen las cosas"
              aria-label="Procedimientos"
              className="hidden md:grid place-items-center w-[46px] h-[46px] rounded-full bg-card text-muted-foreground hover:text-foreground shadow-[0_1px_3px_rgba(17,19,24,0.06)] transition-colors"
            >
              <Icon name="help" className="text-[22px]" />
            </Link>

            {/* New task — fires global event, listeners on Kanban/Tareas can react */}
            <Button
              className="h-[44px] sm:h-[46px] gap-2 px-3 sm:pl-4 sm:pr-5 text-[15px] font-extrabold flex-shrink-0"
              onClick={() => window.dispatchEvent(new CustomEvent("crew-action", { detail: "new-task" }))}
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline">Nueva tarea</span>
            </Button>
          </div>
        </header>

        {/* Page content (en celular con la barra inferior hay 84 px de despeje) */}
        <PushEnApp />
        <main className={cn("flex-1 overflow-auto", barraInferiorOn && "max-md:pb-[calc(84px+env(safe-area-inset-bottom))]")}>
          <PushInvitacion />
          <Outlet />
        </main>
        <BarraInferior onYo={() => setYoOpen(true)} chat={chatResumen} />
        <PantallaYo abierta={yoOpen} onCerrar={() => setYoOpen(false)} onVerTodo={() => setMobileOpen(true)} />
      </div>

      {/* v6.7.6 F5 · Toast XP global */}
      <XpToast />

      {/* Academia SOA 10 PRO · tour de la primera vez (detrás de ff_academia) */}
      <TourAutoStart />

      {/* Temporada 1 · confetti + banner al subir de nivel o ganar badge */}
      <GamifCelebration />

      {/* Command palette */}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />

      {/* Crew Brain — AI chat con tu data (atajo Ctrl+J) */}
      <CrewBrain />
      <MotivoCambioHost />
      <EsfuerzoHost />
      <RetiroHost />

      {/* Modales globales — escuchan "crew-action" events */}
      <CrearTareaModal />
      <CrearClienteModal />
      <CrearProyectoModal />

      {/* Notifications manager — invisible · polling central + dispatcher
          (toast / sonido / desktop / title bar / favicon) */}
      <NotificationsManager />
    </div>
    </AcademiaProvider>
  );
}

/* ── Cabecera de grupo plegable: 34px, 13px 800, chevron + contador al plegar ── */
function GrupoHeader({ label, abierto, cantidad, onClick, tono }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={abierto}
      className={cn(
        "w-full flex items-center gap-1.5 h-[34px] px-3 rounded-xl text-[13px] font-extrabold select-none hover:bg-card/70 transition-colors",
        tono === "amber" ? "text-[#B45309]" : "text-[#6B6F7A]"
      )}
    >
      {abierto ? <ChevronDown className="h-[18px] w-[18px] flex-shrink-0" /> : <ChevronRight className="h-[18px] w-[18px] flex-shrink-0" />}
      <span className="flex-1 text-left truncate">{label}</span>
      {!abierto && (
        <span className="min-w-[26px] h-[22px] px-2 rounded-full bg-[#E7E8EC] text-[#4B4F59] text-[12px] font-extrabold grid place-items-center tabular-nums">
          {cantidad}
        </span>
      )}
    </button>
  );
}

// Academia · marcas del tour sobre la barra lateral (solo un atributo: no cambian estilos ni estructura)
const TOUR_NAV = { "/inicio": "mi-dia-nav", "/tareas?mis=1": "nav-mis-tareas" };

/* ── Nav item: 40px, radio 14, 15px. Activo: tarjeta blanca + #0E7490 800 + ícono relleno ── */
function NavItem({ to, label, icon: Icon, shortcut, collapsed, badge, badgeColor = "amber", nuevo, isFavorite, onToggleFavorite }) {
  const { esNueva, marcarVista } = useNewFeature(nuevo || "__none__");
  const BADGE_CLS = {
    amber:   "bg-[#FDE68A] text-[#92400E]",
    purple:  "bg-[#EDE9FE] text-[#6D28D9]",
    rose:    "bg-[#FFE4E6] text-[#BE123C]",
    blue:    "bg-[#DBEAFE] text-[#1D4ED8]",
    fuchsia: "bg-[#FAE8FF] text-[#A21CAF]",
    emerald: "bg-[#DCFCE7] text-[#15803D]",
  }[badgeColor] || "bg-[#FDE68A] text-[#92400E]";
  return collapsed ? (
    <Tooltip content={label} side="right">
      <NavLink
        to={to}
        data-tour={TOUR_NAV[to]}
        onClick={() => trackNav(to, "sidebar")}
        className={({ isActive }) =>
          cn(
            "flex items-center justify-center w-12 h-11 rounded-[14px] mx-auto transition-all relative",
            isActive
              ? "bg-card text-[#0E7490] shadow-[0_1px_3px_rgba(17,19,24,0.08)]"
              : "text-[#6B6F7A] hover:bg-card/70 hover:text-foreground"
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon className="text-[24px]" fill={isActive} />
            {badge ? <span className="absolute top-1 right-1.5 h-2.5 w-2.5 rounded-full bg-[#F59E0B]" /> : null}
          </>
        )}
      </NavLink>
    </Tooltip>
  ) : (
    <NavLink
      to={to}
      data-tour={TOUR_NAV[to]}
      onClick={() => { trackNav(to, "sidebar"); if (nuevo) marcarVista(); }}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 h-10 px-3 rounded-[14px] text-[15px] transition-all group relative",
          isActive
            ? "bg-card text-[#0E7490] font-extrabold shadow-[0_1px_3px_rgba(17,19,24,0.08)]"
            : "text-[#2B2E36] font-semibold hover:bg-card/70"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("text-[21px] flex-shrink-0", isActive ? "text-[#0E7490]" : "text-[#6B6F7A]")} fill={isActive} />
          <span className="flex-1 truncate">{label}</span>
          {nuevo && esNueva && (
            <span
              onClick={() => marcarVista()}
              className="text-[12px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#10B981] text-white leading-none"
            >
              Nuevo
            </span>
          )}
          {badge && (
            <span className={cn("text-[12px] font-extrabold px-2 h-6 rounded-full min-w-[24px] grid place-items-center tabular-nums", BADGE_CLS)}>
              {badge}
            </span>
          )}
          {shortcut && !badge && !onToggleFavorite && (
            <kbd className="text-[12px] text-muted-foreground/60 rounded px-1 py-px bg-muted opacity-0 group-hover:opacity-100 transition-opacity font-mono">
              {shortcut}
            </kbd>
          )}
          {onToggleFavorite && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(to); }}
              className={cn(
                "flex-shrink-0 p-0.5 rounded transition-all",
                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                isFavorite
                  ? "text-[#F59E0B] hover:text-[#D97706]"
                  : "text-muted-foreground/50 hover:text-[#F59E0B]"
              )}
              title={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
              aria-label={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
            >
              <Star className={cn("h-4 w-4", isFavorite && "fill-current")} />
            </button>
          )}
        </>
      )}
    </NavLink>
  );
}
